import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, Truck, ArrowRight } from "lucide-react";
import { useStore, FREE_SHIPPING_THRESHOLD } from "../context/StoreContext";
import { formatBRL } from "../api";

export function CartDrawer() {
  const { cart, cartOpen, setCartOpen, updateQty, removeItem, subtotal, discount, coupon, applyCoupon, setCheckoutOpen } = useStore();
  const [couponInput, setCouponInput] = useState("");
  const progress = Math.min(subtotal / FREE_SHIPPING_THRESHOLD, 1);
  const missing = Math.max(FREE_SHIPPING_THRESHOLD - subtotal, 0);

  return (
    <AnimatePresence>
      {cartOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setCartOpen(false)}
            className="fixed inset-0 z-50 bg-slate-950/50 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="vult-cart-drawer"
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", stiffness: 300, damping: 32 }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col bg-white shadow-2xl"
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-5">
              <h3 className="font-display text-2xl font-black uppercase text-slate-900">Seu carrinho</h3>
              <button data-testid="vult-cart-close-btn" onClick={() => setCartOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 transition-colors hover:bg-slate-900 hover:text-white">
                <X size={18} />
              </button>
            </div>

            <div className="border-b border-slate-100 px-6 py-4">
              <div className="mb-2 flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-600">
                <Truck size={13} className="text-vult-blue" />
                {missing > 0 ? `Faltam ${formatBRL(missing)} para frete grátis` : "Você ganhou frete grátis!"}
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                <motion.div
                  className={`h-full rounded-full ${missing > 0 ? "bg-vult-blue" : "bg-vult-lime"}`}
                  animate={{ width: `${progress * 100}%` }}
                  transition={{ type: "spring", stiffness: 80, damping: 20 }}
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-4">
              {cart.length === 0 ? (
                <div data-testid="vult-cart-empty" className="flex h-full flex-col items-center justify-center text-center">
                  <div className="font-display text-2xl font-black uppercase text-slate-300">Carrinho vazio</div>
                  <p className="mt-1 text-sm text-slate-500">Escolha um par e volte aqui.</p>
                </div>
              ) : (
                <ul className="space-y-4">
                  {cart.map((item) => (
                    <li key={item.key} data-testid={`vult-cart-item-row-${item.key}`} className="flex gap-4 rounded-2xl border border-slate-100 bg-[#F8F9FA] p-3">
                      <img src={item.image} alt={item.name} className="h-20 w-20 rounded-xl object-cover" />
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="font-display text-base font-bold uppercase leading-tight text-slate-900">{item.name}</div>
                            <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-slate-500">Tam {item.size}{item.color ? ` • ${item.color}` : ""} • {item.brand}</div>
                          </div>
                          <button data-testid={`vult-cart-item-remove-btn-${item.key}`} onClick={() => removeItem(item.key)} className="text-slate-400 transition-colors hover:text-vult-red">
                            <Trash2 size={16} />
                          </button>
                        </div>
                        <div className="mt-auto flex items-center justify-between pt-2">
                          <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-1.5 py-1">
                            <button data-testid={`vult-cart-qty-minus-${item.key}`} onClick={() => updateQty(item.key, -1)} className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-slate-100"><Minus size={12} /></button>
                            <span className="w-5 text-center font-mono text-xs font-bold">{item.qty}</span>
                            <button data-testid={`vult-cart-qty-plus-${item.key}`} onClick={() => updateQty(item.key, 1)} className="flex h-6 w-6 items-center justify-center rounded-full hover:bg-slate-100"><Plus size={12} /></button>
                          </div>
                          <span className="font-display text-lg font-black text-slate-900">{formatBRL(item.price * item.qty)}</span>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {cart.length > 0 && (
              <div className="border-t border-slate-200 px-6 py-5">
                {!coupon ? (
                  <div className="mb-4 flex gap-2">
                    <input
                      data-testid="vult-cart-coupon-input"
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value)}
                      placeholder="Cupom (ex: VULT10)"
                      className="flex-1 rounded-full border border-slate-300 px-4 py-2 font-mono text-xs uppercase outline-none focus:border-slate-900"
                    />
                    <button
                      data-testid="vult-cart-coupon-apply-btn"
                      onClick={() => applyCoupon(couponInput)}
                      className="rounded-full border border-slate-900 px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest transition-colors hover:bg-slate-900 hover:text-white"
                    >
                      Aplicar
                    </button>
                  </div>
                ) : (
                  <div data-testid="vult-cart-coupon-applied" className="mb-4 flex items-center justify-between rounded-full bg-vult-lime px-4 py-2 font-mono text-xs font-bold uppercase tracking-widest text-slate-900">
                    Cupom {coupon.code} aplicado <span>-{formatBRL(discount)}</span>
                  </div>
                )}
                <div className="mb-1 flex justify-between text-sm text-slate-600">
                  <span>Subtotal</span><span>{formatBRL(subtotal)}</span>
                </div>
                {discount > 0 && (
                  <div className="mb-1 flex justify-between text-sm font-bold text-emerald-600">
                    <span>Desconto</span><span>-{formatBRL(discount)}</span>
                  </div>
                )}
                <div className="mb-4 flex justify-between font-display text-2xl font-black text-slate-900">
                  <span>Total</span><span data-testid="vult-cart-total">{formatBRL(subtotal - discount)}</span>
                </div>
                <button
                  data-testid="vult-cart-checkout-proceed-btn"
                  onClick={() => { setCartOpen(false); setCheckoutOpen(true); }}
                  className="group flex w-full items-center justify-center gap-2 rounded-full bg-vult-red py-4 font-mono text-sm font-bold uppercase tracking-widest text-slate-900 transition-colors hover:bg-slate-900 hover:text-white"
                >
                  Finalizar compra <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
